<script setup>
  import {computed, inject} from "vue";

  import ComponentFoldable from "@components/Foldable/Foldable.vue";
  import ComponentPatientSidebarPatientinputFoldableComplianceBar from "@components/Sidebars/PatientSidebar/PatientinputFoldableComplianceBar.vue";
  import {recordOriginKeys, recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentPatientSidebarPatientinputFoldable",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["open:address-book"]);

  const privacy = inject("$privacy");

  const foldableTitle = computed(() => {
    return props.record.origin === recordOriginKeys.patientinput
      ? recordOriginTexts.patientinput
      : recordOriginTexts.carerinput;
  });

  const nursingHomeAttributes = computed(() => {
    const attributes_ = {
      name: "Name",
      street: "Straße, Hausnr.",
      zip: "Postleitzahl",
      city: "Stadt",
    };

    return Object.keys(attributes_)
      .filter((key) => props.record.metadata?.patientinput.nursing_home[key])
      .reduce((object, key) => {
        object[key] = attributes_[key];
        return object;
      }, {});
  });

  const attributes = computed(() => {
    const attributes_ = {
      contactdata: "Kontaktperson",
      doc_name: "Behandelnder Arzt / behandelnde Ärztin",
      nursing_home: "Einrichtung",
      compliance: "Compliance",
    };

    return Object.keys(attributes_)
      .filter((key) => {
        if (key === "nursing_home") return Object.keys(nursingHomeAttributes.value).length > 0;
        else if (key === "compliance") return props.record.metadata.patientinput.is_patient;
        else return key !== "compliance" && props.record.metadata.patientinput[key];
      })
      .reduce((object, key) => {
        object[key] = attributes_[key];
        return object;
      }, {});
  });

  const badgeLabel = computed(() => Object.keys(attributes.value).length);

  const showFoldable = computed(() => {
    return (
      [recordOriginKeys.patientinput, recordOriginKeys.carerinput].includes(props.record.origin) &&
      Object.keys(attributes.value).length > 0
    );
  });

  const openPatientAddressBookDialog = async (search = null) => {
    if (typeof search === "string") {
      search = await privacy.decryptValue(search);
    } else if (typeof search === "object") {
      const keys = Object.keys(search);
      const values = await Promise.all(keys.map((key) => privacy.decryptValue(search[key])));
      search = Object.fromEntries(keys.map((key, index) => [key, values[index]]));
      // rename the key "name" into "company" (as used in our address book)
      const {name, ...rest} = search;
      search = {company: name, ...rest};
    }

    emit("open:address-book", {search: search});
  };
</script>

<template>
  <ComponentFoldable
    v-if="showFoldable"
    :title="foldableTitle"
    :badge-label="badgeLabel"
    badge-color="blue"
    :test-id="getTestId('patient-input')"
  >
    <div>
      <ul class="space-y-4">
        <template v-for="(title, key) in attributes" :key="key">
          <li v-if="key === 'contactdata'">
            <h6 class="text-xs text-gray-600">{{ title }}</h6>
            <div
              v-if="record.metadata.patientinput.contactdata['name']"
              :data-encrypted="record.metadata.patientinput.contactdata['name']"
            />

            <div
              v-if="record.metadata.patientinput.contactdata['telephone']"
              :data-encrypted="record.metadata.patientinput.contactdata['telephone']"
            />

            <template v-if="true">
              <!-- TODO find solution for Link becuase data-encrypted is not working with <a> (removes the underline) -->
              <MedLink
                variant="primary"
                href="mailto:"
                :data-encrypted="record.metadata.patientinput.contactdata['email']"
                :data-test="getTestId('patientinput-foldable-email-link')"
                @mousedown="
                  (event) => {
                    const target = event.target;
                    if (target.tagName === 'A') {
                      const textNode = target.textContent;
                      if (textNode) {
                        target.href = `mailto:${textNode}`;
                      }
                    }
                  }
                "
              ></MedLink>
            </template>
          </li>

          <li v-else-if="key === 'nursing_home'">
            <div class="relative bg-mcred-50 p-2 text-sm shadow-md">
              <h6 class="text-xs text-gray-600">{{ title }}</h6>
              <div :data-encrypted="record.metadata.patientinput.nursing_home['name']" />
              <div :data-encrypted="record.metadata.patientinput.nursing_home['street']" />
              <div>
                <span :data-encrypted="record.metadata.patientinput.nursing_home['zip']" />
                <span :data-encrypted="record.metadata.patientinput.nursing_home['city']" />
              </div>

              <MedButton
                variant="primary"
                icon="add"
                size="xs"
                rounded="full"
                class="absolute right-2 bottom-2"
                :data-test="getTestId('patient-input-foldable-open-address-nurse-dialog-button')"
                @click="openPatientAddressBookDialog(record.metadata.patientinput.nursing_home)"
              />
            </div>
          </li>

          <li v-else-if="key === 'compliance'">
            <h6 class="text-xs text-gray-600">{{ title }}</h6>

            <div class="mt-0.5 flex flex-col gap-2 text-sm">
              <div>
                <p class="mb-1">Umgang mit Medikation</p>

                <ComponentPatientSidebarPatientinputFoldableComplianceBar
                  :current="record.metadata.patientinput.compliance.confident - 1"
                >
                  <span>nicht sicher</span>
                  <span>sehr sicher</span>
                </ComponentPatientSidebarPatientinputFoldableComplianceBar>
              </div>

              <div>
                <p class="mb-1">Einnahme vergessen (Häufigkeit)</p>

                <ComponentPatientSidebarPatientinputFoldableComplianceBar
                  :current="record.metadata.patientinput.compliance.forgotten - 1"
                >
                  <span>noch nie</span>
                  <span>sehr oft</span>
                </ComponentPatientSidebarPatientinputFoldableComplianceBar>
              </div>

              <div>
                <p class="mb-1">Alltags-Beeinträchtigung</p>

                <ComponentPatientSidebarPatientinputFoldableComplianceBar
                  :current="record.metadata.patientinput.compliance.affect - 1"
                >
                  <span>überhaupt nicht</span>
                  <span>völlig</span>
                </ComponentPatientSidebarPatientinputFoldableComplianceBar>
              </div>

              <div>
                <p class="mb-1">Schmerzen</p>

                <ComponentPatientSidebarPatientinputFoldableComplianceBar
                  :current="record.metadata.patientinput.compliance.pain - 1"
                  :max="9"
                >
                  <span>kein Schmerz</span>
                  <span>extremer Schmerz</span>
                </ComponentPatientSidebarPatientinputFoldableComplianceBar>
              </div>
            </div>
          </li>

          <li v-else-if="key === 'doc_name' && record.metadata.patientinput[key]">
            <div class="relative bg-mcred-50 p-2 text-sm shadow-md">
              <h6 class="text-xs text-gray-600">{{ title }}</h6>
              <p :data-encrypted="record.metadata.patientinput[key]" />

              <template v-if="key === 'doc_name'">
                <MedButton
                  variant="primary"
                  icon="add"
                  size="xs"
                  rounded="full"
                  class="absolute right-2 bottom-2"
                  :data-test="getTestId('patient-input-foldable-open-address-dialog-button')"
                  @click="openPatientAddressBookDialog(record.metadata.patientinput[key])"
                />
              </template>
            </div>
          </li>
        </template>
      </ul>
    </div>
  </ComponentFoldable>
</template>

<style scoped></style>
